<template>
  <div class="m-0 p-1">

    <div class="mt-6 px-4 flex justify-between items-center w-full">
      <div>
        <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">
          {{ t('apiKeysPage.title') }}
        </h1>
        <h1 class="text text-gray-900 dark:text-gray-400">
          {{ t('apiKeysPage.subtitle') }}
        </h1>
      </div>

      <div class="flex items-center">
         <label class="input input-bordered flex items-center" style="width:330px">
            <input type="text" v-model="searchValue" class="grow" :placeholder="t('apiKeysPage.searchPlaceholder')" />
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="h-4 w-4 opacity-70">
              <path fill-rule="evenodd"
                d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
                clip-rule="evenodd" />
            </svg>
         </label>
        <button class="btn" style="margin-inline-start: 1rem;" @click="openCreateKeyModal()">
           <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" style="margin-inline-end: 0.25rem;" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" /></svg>
          {{ t('apiKeysPage.addNewKeyButton') }}
        </button>
      </div>
    </div>

    <div class="mt-6 px-4">
      <ThemeOnly theme="modern" :invert="true">
        <div class="overflow-x-auto text-align card shadow bg-base-100 dark:bg-gray-800 w-full rounded-md p-3 border dark:border-gray-700 flex flex-col"
          style="border-radius: 10px; min-height: 60vh;"> 
      
         <div v-if="isDefinedAndNotEmpty(loadingError) === true" class="p-4 text-center text-error bg-error/10 rounded-t-lg">
            {{ loadingError }}
         </div>
         <div v-if="isDefinedAndNotEmpty(revokeError) === true" class="p-4 text-center text-error bg-error/10">
             {{ revokeError }}
         </div>
       
      <EasyDataTable
        :headers="headers"
        :items="apiKeys"
        :loading="isLoading"
        :search-field="searchFields"
        :search-value="searchValue"
        table-class-name="table w-full table-auto" 
        header-text-direction="left" 
        body-text-direction="left" 
        theme-color="#4f46e5" 
        buttons-pagination
        :empty-message="t('apiKeysPage.emptyMessage')"
        :rows-per-page-message="t('apiKeysPage.rowsPerPageMessage')"
        class="flex-1"
      >
        <template #loading>
          <div class="flex justify-center items-center p-10">
            <ModernLoader />
          </div>
        </template>

        <template #item-createdAt="item">
          {{ formatDate(item.createdAt) }}
        </template>

        <template #item-expiresAt="item">
          {{ formatDate(item.expiresAt) }}
        </template>

        <template #item-status="item">
          <span :class="['badge badge-sm', item.status === 'active' ? 'badge-success' : 'badge-error']">
            {{ item.status }}
          </span>
        </template>

        <template #item-actions="item">
           <button
             v-if="item.status === 'active'"
             @click="openRevokeConfirm(item.apiKeyId)" 
             class="btn btn-error btn-xs" 
             :disabled="isRevoking[item.apiKeyId] === true" 
           >
             <span v-if="isRevoking[item.apiKeyId] === true" class="loading loading-spinner loading-xs"></span>
             <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
             <span class="ml-1">Revoke</span>
           </button>
           <span v-else class="text-gray-400 italic text-xs">Revoked</span>
         </template>

      </EasyDataTable>
        </div>
      </ThemeOnly>
    </div>


  <it-modal v-model="showCreateKeyModal">
    <div class="p-4">
      <h3 class="font-bold text-lg mb-4">{{ t('apiKeysPage.modal.createTitle') }}</h3>
      <form @submit.prevent="createKey">
        <div class="form-control mb-4">
          <label for="apiKeyName" class="label">
            <span class="label-text">{{ t('apiKeysPage.modal.keyNameLabel') }}</span>
          </label>
          <input
            type="text"
            id="apiKeyName"
            v-model="newKeyName"
            :placeholder="t('apiKeysPage.modal.keyNamePlaceholder')"
            class="input input-bordered w-full"
            required
          />
        </div>
        <div class="form-control mb-4">
          <label for="keyDuration" class="label">
            <span class="label-text">{{ t('apiKeysPage.modal.durationLabel') }}</span>
          </label>
          <select id="keyDuration" v-model="newKeyDurationDays" class="select select-bordered w-full">
            <option :value="7">7 Days</option>
            <option :value="30">30 Days</option>
            <option :value="90">90 Days</option>
            <option :value="365">1 Year</option>
          </select>
        </div>
        <p v-if="creationError !== null" class="text-error text-sm mb-4">{{ creationError }}</p>
        <div class="flex justify-end mt-4">
          <button type="button" class="btn mr-2" @click="showCreateKeyModal = false; creationError = null;">{{ t('apiKeysPage.modal.cancelButton') }}</button>
          <button type="submit" class="btn" :disabled="isCreating !== false">
            <span v-if="isCreating === true" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('apiKeysPage.modal.createButton') }}
          </button>
        </div>
      </form>
    </div>
  </it-modal>

  <it-modal v-model="showGeneratedKeyModal">
    <div class="p-4">
      <h3 class="font-bold text-lg mb-4">{{ t('apiKeysPage.generatedModal.title') }}</h3>
      <p class="mb-2">{{ t('apiKeysPage.generatedModal.message') }}</p>
      <div class="bg-base-200 p-3 rounded mb-4 break-all font-mono text-sm">
        <code>{{ generatedKey }}</code>
      </div>
      <div class="flex justify-end mt-4"> 
        <button class="btn mr-2" @click="copyKeyToClipboard">{{ t('apiKeysPage.generatedModal.copyButton') }}</button> 
        <button class="btn" @click="closeGeneratedKeyModal">{{ t('apiKeysPage.generatedModal.closeButton') }}</button>
      </div>
    </div> 
  </it-modal>

  <it-modal v-model="showRevokeConfirmModal">
    <div class="p-4">
      <h3 class="font-bold text-lg mb-2">{{ t('apiKeysPage.confirmRevokeModal.title') }}</h3>
      <p class="py-4">{{ t('apiKeysPage.confirmRevokeModal.text') }}</p>
      <div class="flex justify-end mt-4"> 
        <button class="btn" @click="showRevokeConfirmModal = false; keyToRevokeId = null;">{{ t('apiKeysPage.confirmRevokeModal.cancelButton') }}</button>
        <button class="btn btn-error" @click="confirmRevoke">{{ t('apiKeysPage.confirmRevokeModal.confirmButton') }}</button>
      </div>
    </div> 
  </it-modal>

</div>
</template>

<style scoped>
.card {

}

:deep(.vue3-easy-data-table__main) {
  flex-grow: 1; 
  min-height: 40vh; 
}

:deep(.vue3-easy-data-table__header th) {

  font-weight: 600;
  color: #374151;
  text-align: left;
  padding-left: 1rem; 
  padding-right: 1rem;
}

:deep(.vue3-easy-data-table__body td) {
   padding-left: 1rem;
   padding-right: 1rem;
   text-align: left; 
}

.badge {
  text-transform: capitalize;
}


.modal:not(dialog:not([open])) {
  display: flex;
}


</style>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import axios from '../api/axiosInstance'; 
import { globalState } from '@/globalState'; 
import { useToast } from 'vue-toastification';
import Vue3EasyDataTable from 'vue3-easy-data-table';
import 'vue3-easy-data-table/dist/style.css';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
import { useI18n } from '@/i18n';
import ModernLoader from '@/components/ModernLoader.vue';
import ThemeOnly from '@/components/ThemeOnly.vue';

const searchFields = ["keyName"];
const searchValue = ref("");
const { t } = useI18n();

const headers = [
  { text: t('apiKeysPage.tableHeaders.name'), value: "keyName", sortable: true },
  { text: t('apiKeysPage.tableHeaders.keyId'), value: "apiKeyId" },
  { text: t('apiKeysPage.tableHeaders.status'), value: "status", sortable: true },
  { text: t('apiKeysPage.tableHeaders.createdAt'), value: "createdAt", sortable: true },
  { text: t('apiKeysPage.tableHeaders.expiresAt'), value: "expiresAt", sortable: true },
  { text: t('apiKeysPage.tableHeaders.actions'), value: "actions" }
];

const apiKeys = ref([]);
const isLoading = ref(false);
const isCreating = ref(false);
const isRevoking = reactive({}); 
const loadingError = ref(null);
const creationError = ref(null);
const revokeError = ref(null); 

const showCreateKeyModal = ref(false);
const newKeyName = ref('');
const newKeyDurationDays = ref(30);

const showGeneratedKeyModal = ref(false);
const generatedKey = ref('');

const showRevokeConfirmModal = ref(false);
const keyToRevokeId = ref(null);

const apiEndpoint = globalState.apiEndpoint;
const toast = useToast();

const normalizeApiKey = (item) => {
  return {
    apiKeyId: item?.apiKeyId ?? item?.apikeyid ?? '',
    keyName: item?.keyName ?? item?.keyname ?? '',
    status: item?.status ?? '',
    createdAt: item?.createdAt ?? item?.createdat ?? null,
    expiresAt: item?.expiresAt ?? item?.expiresat ?? null
  };
};

const getAuthToken = () => {
  const cookies = document.cookie.split('; ');
  const authCookie = cookies.find(cookie => cookie.startsWith('authToken=') === true);
  const jwtCookie = cookies.find(cookie => cookie.startsWith('jwtToken=') === true);
  const cookie = authCookie !== undefined && authCookie !== null ? authCookie : jwtCookie;
  if (cookie === undefined || cookie === null) {
    return '';
  }
  const token = cookie.split('=')[1];
  return token !== undefined && token !== null && token !== '' ? token : '';
};

const fetchApiKeys = async () => {
  isLoading.value = true;
  loadingError.value = null;
  revokeError.value = null;
  apiKeys.value = []; 
  try {
    const response = await axios.get(`${apiEndpoint}/api/apikeys`);
    const responseData = response.data;
    const rawItems = Array.isArray(responseData) === true
      ? responseData
      : (Array.isArray(responseData?.Body) === true
        ? responseData.Body
        : (Array.isArray(responseData?.body) === true ? responseData.body : []));
    apiKeys.value = rawItems.map(item => normalizeApiKey(item));
  } catch (error) {
    console.error('Error fetching API keys:', error);
    loadingError.value = isDefinedAndNotEmpty(error.response?.data?.message) != false ? error.response?.data?.message : (isDefinedAndNotEmpty(error.message) != false ? error.message : 'Failed to load API keys.');
    if (toast !== null && toast !== undefined) {
      toast.error(loadingError.value);
    }
    if (error.response?.status === 401 || error.response?.status === 403) {
        console.error("Redirecting to login due to auth error.");
    }
  } finally {
    isLoading.value = false;
  }
};

const createKey = async () => {
  if (newKeyName.value.trim().length === 0) {
    creationError.value = 'Please provide a name for the API key.';
    if (toast !== null && toast !== undefined) {
      toast.warning(creationError.value);
    }
    return;
  }
  isCreating.value = true;
  creationError.value = null;
  try {
    const response = await axios.post(`${apiEndpoint}/api/apikeys`, { 
      name: newKeyName.value.trim(),
      durationDays: newKeyDurationDays.value
    });

    generatedKey.value = response.data.apiKey; 
    showCreateKeyModal.value = false; 
    showGeneratedKeyModal.value = true; 
    newKeyName.value = ''; 
    newKeyDurationDays.value = 30;
    if (toast !== null && toast !== undefined) {
      toast.success(isDefinedAndNotEmpty(response.data.message) != false ? response.data.message : 'API Key created successfully!');
    }
  } catch (error) {
    console.error('Error creating API key:', error);
    creationError.value = isDefinedAndNotEmpty(error.response?.data?.message) != false ? error.response?.data?.message : (isDefinedAndNotEmpty(error.message) != false ? error.message : 'Failed to create API key.');
    if (toast !== null && toast !== undefined) {
      toast.error(creationError.value);
    }
  } finally {
    isCreating.value = false;
  }
};

const openRevokeConfirm = (keyId) => {
  keyToRevokeId.value = keyId;
  revokeError.value = null; 
  showRevokeConfirmModal.value = true;
};

const confirmRevoke = async () => {
  const keyId = keyToRevokeId.value;
  if (keyId === null) {
    console.error("No key ID selected for revocation.");
    return;
  }

  isRevoking[keyId] = true;
  revokeError.value = null; 
  showRevokeConfirmModal.value = false; 

  try {
    await axios.delete(`${apiEndpoint}/api/apikeys/${keyId}`);
    
    const keyIndex = apiKeys.value.findIndex(k => k.apiKeyId === keyId);
    if (keyIndex !== -1) {
      apiKeys.value[keyIndex].status = 'revoked';
    }
    if (toast !== null && toast !== undefined) {
      toast.success('API Key marked as revoked successfully.');
    }
  } catch (error) {
    console.error('Error revoking API key:', error);
    revokeError.value = isDefinedAndNotEmpty(error.response?.data?.message) != false ? error.response.data.message : (isDefinedAndNotEmpty(error.message) != false ? error.message : 'Failed to revoke API key.');
    if (toast !== null && toast !== undefined) {
      toast.error(revokeError.value);
    }
  } finally {
    delete isRevoking[keyId];
    keyToRevokeId.value = null;
  }
};

const closeGeneratedKeyModal = () => {
  generatedKey.value = '';
  showGeneratedKeyModal.value = false;
  fetchApiKeys();
};

const copyKeyToClipboard = async () => {
  if (generatedKey.value === null || generatedKey.value === undefined || generatedKey.value === '') {
    return;
  }
  try {
    await navigator.clipboard.writeText(generatedKey.value);
    if (toast !== null && toast !== undefined) {
      toast.info('API Key copied to clipboard!');
    }
  } catch (err) {
    console.error('Failed to copy key:', err);
    if (toast !== null && toast !== undefined) {
      toast.error('Failed to copy key to clipboard.');
    }
  }
};

const formatDate = (dateString) => {
  if (dateString === null || dateString === undefined) {
    return 'N/A';
  }
  try {
    const date = new Date(dateString);
     if (isNaN(date.getTime())) {
         console.warn('Invalid date string received:', dateString);
         return 'Invalid Date';
     }
    return date.toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch (error) {
    console.warn('Error formatting date:', dateString, e);
    return String(dateString);
  }
};


const openCreateKeyModal = () => {
  newKeyName.value = ''; 
  newKeyDurationDays.value = 30;
  creationError.value = null; 
  showCreateKeyModal.value = true; 
};

onMounted(() => {
  fetchApiKeys();
});
</script>
